import React, { useRef, useState, useLayoutEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Divider,
    CircularProgress,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { Relatorio } from "./Relatorio";
import { useReactToPrint } from "react-to-print";
const todosCampos: { campo: string; label: string }[] = [
    { campo: "id", label: "ID" },
    { campo: "nome", label: "Nome" },
    { campo: "cpf", label: "CPF" },
    { campo: "cnpj", label: "CNPJ" },
    { campo: "email", label: "Email" },
    { campo: "telefone", label: "Telefone" },
    { campo: "sexo", label: "Sexo" },
    { campo: "data_nascimento", label: "Nascimento" },
    { campo: "data_cadastro", label: "Cadastro" },
    { campo: "ultimo_login", label: "Último Login" },
    { campo: "perfil", label: "Perfil" },
    { campo: "servidor", label: "Servidor" },
    { campo: "administrador", label: "Administrador" },
    { campo: "inativo", label: "Inativo" },
    { campo: "descricao", label: "Descrição" },
    { campo: "endereco", label: "Endereço" },
    { campo: "bairro", label: "Bairro" },
    { campo: "cidade", label: "Cidade" },
    { campo: "uf", label: "UF" },
    { campo: "cep", label: "CEP" },
    { campo: "zona_rural", label: "Zona Rural" },
    { campo: "localidade_rural", label: "Localidade Rural" },
    { campo: "observacoes", label: "Observações" },
];

// Largura mínima da "folha" na pré-visualização (px)
const LARGURA_MIN_FOLHA = 480;

// ✅ Interface para tipagem dos props
interface FooterProps {
    status: string;
    servidor: string;
    sexo: string;
    zonaRural: string;
    idadeMin: string;
    idadeMax: string;
    profissao: string;
    bairro: string;
    cadastroInicio: string;
    cadastroFim: string;
    searchTerm: string;
    total: number;
    obterRegistros: () => Promise<any[]>;
    /**
     * Quando informado, mostra o botão "Novo usuário" nesta barra.
     * Fica undefined para quem não pode criar (não-administrador), então o
     * botão nem existe — quem controla a permissão é a tela, não o rodapé.
     */
    onNovoUsuario?: () => void;
}

export function Footer(props: FooterProps) {

    const [open, setOpen] = useState(false);
    const [registros, setRegistros] = useState<any[]>([]);
    const [gerando, setGerando] = useState(false);
    const [carregandoPreview, setCarregandoPreview] = useState(false);
    const [camposSelecionados, setCamposSelecionados] = useState<string[]>([
        "nome",
        "cpf",
        "cnpj",
        "email",
        "telefone",
        "data_nascimento"
    ]);

    // Campos ordenados pela ordem canônica (todosCampos), não pela ordem de seleção.
    // Assim, ao marcar o ID depois, ele aparece na 1ª coluna — na posição certa.
    const camposOrdenados = useMemo(
        () => todosCampos.map(c => c.campo).filter(campo => camposSelecionados.includes(campo)),
        [camposSelecionados]
    );

    const contentRef = useRef<HTMLDivElement>(null);
    const reactToPrintFn = useReactToPrint({ contentRef });

    // --- Escala dinâmica do preview -------------------------------------
    // A folha usa width:max-content, então mede a largura/altura REAIS do
    // conteúdo (offsetWidth/offsetHeight não são afetados pelo transform) e
    // escala para caber na largura disponível. Sem loop, sem "gigante".
    const previewViewportRef = useRef<HTMLDivElement>(null);
    const paginaRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [wrapperWidth, setWrapperWidth] = useState<number>(0);
    const [wrapperHeight, setWrapperHeight] = useState<number>(0);

    const recalcularEscala = useCallback(() => {
        const viewport = previewViewportRef.current;
        const pagina = paginaRef.current;
        if (!viewport || !pagina) return;
        const disponivel = viewport.clientWidth - 32; // padding lateral
        const larguraReal = pagina.offsetWidth;
        const alturaReal = pagina.offsetHeight;
        if (larguraReal === 0) return;
        const novaEscala = Math.min(1, disponivel / larguraReal);
        setScale(novaEscala);
        setWrapperWidth(larguraReal * novaEscala);
        setWrapperHeight(alturaReal * novaEscala);
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        recalcularEscala();
        window.addEventListener("resize", recalcularEscala);
        return () => window.removeEventListener("resize", recalcularEscala);
    }, [open, camposOrdenados, registros, carregandoPreview, recalcularEscala]);

    const handleAbrirDialog = async () => {
        setOpen(true);
        setCarregandoPreview(true);
        try {
            const todos = await props.obterRegistros(); // busca TODOS os registros filtrados
            setRegistros(todos);
        } finally {
            setCarregandoPreview(false);
        }
    };
    const handleFecharDialog = () => setOpen(false);

    const handleToggleCampo = (campo: string) => {
        setCamposSelecionados(prev =>
            prev.includes(campo)
                ? prev.filter(c => c !== campo)
                : [...prev, campo]
        );
    };

    const marcarTodos = () => setCamposSelecionados(todosCampos.map(c => c.campo));
    const limparTodos = () => setCamposSelecionados([]);

    const handleImprimir = async () => {
        setGerando(true);
        // Registros já foram carregados na abertura do modal; garante que existam
        if (registros.length === 0) {
            const todos = await props.obterRegistros();
            setRegistros(todos);
        }
        setGerando(false);
        // aguarda o React renderizar o conteúdo oculto antes de imprimir
        setTimeout(() => {
            reactToPrintFn();
        }, 300);
    };

    const relatorioProps = {
        camposSelecionados: camposOrdenados,
        status: props.status,
        servidor: props.servidor,
        sexo: props.sexo,
        zonaRural: props.zonaRural,
        idadeMin: props.idadeMin,
        idadeMax: props.idadeMax,
        profissao: props.profissao,
        bairro: props.bairro,
        cadastroInicio: props.cadastroInicio,
        cadastroFim: props.cadastroFim,
        searchTerm: props.searchTerm,
    };

    return (
        <>
            {/* Barra inline (total + gerar relatório) */}
            <Box
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
            >
                <Typography variant="body2" color="text.secondary">
                    Total:{" "}
                    <Box component="strong" sx={{ color: "text.primary" }}>
                        {props.total}
                    </Box>
                </Typography>

                {/* Hierarquia: "Novo usuário" é a ação principal da tela e leva o
                    preenchimento; "Gerar Relatório" é apoio e fica contornado.
                    Dois botões sólidos lado a lado disputariam a mesma atenção. */}
                {props.onNovoUsuario && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddAlt1Icon />}
                        onClick={props.onNovoUsuario}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                        Novo usuário
                    </Button>
                )}

                <Button
                    variant={props.onNovoUsuario ? "outlined" : "contained"}
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handleAbrirDialog}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                >
                    Gerar Relatório
                </Button>
            </Box>

            {/* Conteúdo oculto usado somente para impressão (todas as páginas) */}
            <div style={{ display: "none" }}>
                <div ref={contentRef}>
                    <Relatorio filteredUsuarios={registros} {...relatorioProps} />
                </div>
            </div>

            {/* Modal: seleção de campos + pré-visualização ao vivo */}
            <Dialog open={open} onClose={handleFecharDialog} maxWidth="xl" fullWidth
                PaperProps={{ sx: { height: "90vh" } }}>
                <DialogTitle sx={{ pb: 1 }}>Selecionar campos do relatório</DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: "flex", overflow: "hidden" }}>
                    {/* Painel esquerdo: campos */}
                    <Box
                        sx={{
                            width: 260,
                            flexShrink: 0,
                            borderRight: 1,
                            borderColor: "divider",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box sx={{ p: 1.5, display: "flex", gap: 1 }}>
                            <Button size="small" onClick={marcarTodos} sx={{ textTransform: "none" }}>
                                Marcar todos
                            </Button>
                            <Button size="small" color="inherit" onClick={limparTodos} sx={{ textTransform: "none" }}>
                                Limpar
                            </Button>
                        </Box>
                        <Divider />
                        <Box sx={{ overflowY: "auto", px: 1.5, py: 1 }}>
                            <FormGroup>
                                {todosCampos.map(({ campo, label }) => (
                                    <FormControlLabel
                                        key={campo}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={camposSelecionados.includes(campo)}
                                                onChange={() => handleToggleCampo(campo)}
                                            />
                                        }
                                        label={label}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    </Box>

                    {/* Painel direito: pré-visualização ao vivo */}
                    <Box
                        ref={previewViewportRef}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "auto",
                            bgcolor: "#e2e8f0",
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                mb: 1.5,
                                width: "fit-content",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                color: "text.secondary",
                                bgcolor: "#e2e8f0",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                Pré-visualização
                                {registros.length > 0 && !carregandoPreview && (
                                    <> — {Math.ceil(registros.length / 18)} página(s)</>
                                )}
                            </Typography>
                        </Box>

                        {carregandoPreview ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, gap: 1.5 }}>
                                <CircularProgress size={22} />
                                <Typography variant="body2" color="text.secondary">
                                    Carregando registros…
                                </Typography>
                            </Box>
                        ) : camposSelecionados.length === 0 ? (
                            <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
                                <Typography variant="body2">
                                    Selecione ao menos um campo para visualizar o relatório.
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: wrapperWidth || undefined,
                                    height: wrapperHeight || undefined,
                                    mx: "auto",
                                }}
                            >
                                <Box
                                    ref={paginaRef}
                                    sx={{
                                        width: "max-content",
                                        minWidth: LARGURA_MIN_FOLHA,
                                        transform: `scale(${scale})`,
                                        transformOrigin: "top left",
                                    }}
                                >
                                    <Relatorio
                                        filteredUsuarios={registros}
                                        modoPreview
                                        {...relatorioProps}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFecharDialog}>Cancelar</Button>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handleImprimir}
                        disabled={camposSelecionados.length === 0 || gerando || carregandoPreview}
                    >
                        {gerando ? "Gerando..." : "Imprimir"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
